/**
 * A product's condition on the website — "Brand New", "UK Premium (Used)",
 * "Second Hand" (Bagz Phones, Oct 2026: "customers can easily identify
 * whether a phone is new or used"). Plain New shows nothing, as before; the
 * rule is lib/config/productCondition#websiteConditionLabel.
 *
 * Green for new, amber for used, blue for refurbished, grey for anything
 * else. On a card it sits on the photo, so it carries its own background.
 */

import { conditionTone, websiteConditionLabel, type ConditionTone } from "@/lib/config/productCondition";

const TONES: Record<ConditionTone, { pill: string; dot: string }> = {
  new: { pill: "border-emerald-200 bg-emerald-50 text-emerald-800", dot: "bg-emerald-500" },
  used: { pill: "border-amber-200 bg-amber-50 text-amber-900", dot: "bg-amber-500" },
  refurbished: { pill: "border-sky-200 bg-sky-50 text-sky-800", dot: "bg-sky-500" },
  other: { pill: "border-gray-200 bg-white text-gray-800", dot: "bg-gray-400" },
};

export function ConditionBadge({
  product,
  size = "sm",
  className = "",
}: {
  product: any;
  /** "sm" on a card's photo, "md" under the name on the product page. */
  size?: "sm" | "md";
  className?: string;
}) {
  const label = websiteConditionLabel(product);
  if (!label) return null;
  const tone = TONES[conditionTone(label)];
  // The page's badge sits on its own line under the name; a card's inline.
  const sizing =
    size === "md"
      ? "flex w-fit gap-1.5 px-2.5 py-1 text-xs"
      : "inline-flex gap-1 px-1.5 py-0.5 text-[9px] sm:text-[10px]";
  return (
    <span
      className={`min-w-0 items-center rounded-full border font-semibold leading-tight shadow-sm ${tone.pill} ${sizing} ${className || "max-w-full"}`}
      title={`Condition: ${label}`}
    >
      <span aria-hidden className={`shrink-0 rounded-full ${tone.dot} ${size === "md" ? "h-1.5 w-1.5" : "h-1 w-1"}`} />
      <span className="truncate">{label}</span>
    </span>
  );
}
